import Phaser from 'phaser';
import { ANIMATION } from '@/config/balance.config';

/**
 * FX destrukcji obiektu — kompozyt 3 warstw:
 *   1. **Flash** — kolorowe okrągłe pole rozszerza się i blaknie (radial burst light).
 *   2. **Main particle burst** — duża chmura cząstek z grawitacją (np. złote monetki).
 *   3. **Accent particle burst** — druga warstwa drobniejszych cząstek (np. biały kurz).
 *   4. **Shockwave** (opcjonalna) — rozszerzający się ring (tylko dla większych obiektów).
 *
 * Preset (`coin`/`wood`/`stone`/`crystal`) definiowany przez `destructible.art.destroyFx`.
 * Wszystkie liczby tunable w `balance.config.ts → ANIMATION.destruction.presets`.
 *
 * Fallback: jeśli preset nie istnieje (np. nowy destructible bez destroyFx),
 * używamy `coin` jako domyślny.
 *
 * Eksportowane są dwa wrapper'y dla wstecznej kompatybilności:
 *   - `spawnCoinBurst(scene, x, y)` — historyczne API, używa preset `coin`
 *   - `spawnDestructionFX(scene, x, y, presetName)` — preferowane nowe API
 */

type PresetName = keyof typeof ANIMATION.destruction.presets;

export function spawnDestructionFX(
  scene: Phaser.Scene,
  x: number,
  y: number,
  presetName: string | undefined,
): void {
  const presetKey = (presetName ?? 'coin') as PresetName;
  const preset = ANIMATION.destruction.presets[presetKey] ?? ANIMATION.destruction.presets.coin;

  // 1) Flash — kolorowe pole które "wybucha" z punktu destrukcji
  spawnRadialFlash(scene, x, y, preset.flashColor, preset.flashRadius, preset.flashDurationMs);

  // 2) Main particle burst
  spawnBurst(scene, x, y, preset.main);

  // 3) Accent particle burst (drobniejsze, więcej feel'u)
  spawnBurst(scene, x, y, preset.accent);

  // 4) Shockwave (opcjonalna, tylko dla mocniejszych destrukcji)
  if (preset.shockwave) {
    spawnShockwave(scene, x, y, preset.shockwave);
  }
}

/** Historyczne API — zostawiamy dla wstecznej kompatybilności. */
export function spawnCoinBurst(scene: Phaser.Scene, x: number, y: number): void {
  spawnDestructionFX(scene, x, y, 'coin');
}

// ─────────────────────────────────────────────────────────────────────────────

type BurstSpec = {
  textureKey: string;
  count: number;
  lifespanMs: number;
  speedMin: number;
  speedMax: number;
  gravityY: number;
  scaleStart: number;
  scaleEnd: number;
};

function spawnBurst(scene: Phaser.Scene, x: number, y: number, spec: BurstSpec): void {
  if (!scene.textures.exists(spec.textureKey)) {
    console.warn(`[DestructionFX] Brak tekstury ${spec.textureKey}`);
    return;
  }
  const emitter = scene.add.particles(x, y, spec.textureKey, {
    speed: { min: spec.speedMin, max: spec.speedMax },
    lifespan: spec.lifespanMs,
    scale: { start: spec.scaleStart, end: spec.scaleEnd },
    angle: { min: 0, max: 360 },
    rotate: { min: 0, max: 360 },
    quantity: spec.count,
    gravityY: spec.gravityY,
    emitting: false,
  });
  emitter.setDepth(20);
  emitter.explode(spec.count, x, y);
  scene.time.delayedCall(spec.lifespanMs + 200, () => emitter.destroy());
}

function spawnRadialFlash(
  scene: Phaser.Scene,
  x: number, y: number,
  color: number,
  radius: number,
  durationMs: number,
): void {
  const flash = scene.add.circle(x, y, radius, color, 0.85);
  flash.setBlendMode(Phaser.BlendModes.ADD);   // additive = bright glow feel
  flash.setDepth(18);
  scene.tweens.add({
    targets: flash,
    alpha: 0,
    scaleX: 1.4,
    scaleY: 1.4,
    duration: durationMs,
    ease: 'Cubic.easeOut',
    onComplete: () => flash.destroy(),
  });
}

type ShockwaveSpec = {
  color: number;
  maxRadius: number;
  lineWidth: number;
  durationMs: number;
};

function spawnShockwave(scene: Phaser.Scene, x: number, y: number, spec: ShockwaveSpec): void {
  // Phaser.Graphics ring rosnący — szybciej niż create+tween na Arc bo bez wypełnienia
  const g = scene.add.graphics();
  g.setDepth(19);

  const start = scene.time.now;
  const handle = scene.time.addEvent({
    delay: 16,
    loop: true,
    callback: () => {
      const elapsed = scene.time.now - start;
      const t = Math.min(1, elapsed / spec.durationMs);
      const r = spec.maxRadius * t;
      const alpha = (1 - t) * 0.85;
      g.clear();
      g.lineStyle(spec.lineWidth, spec.color, alpha);
      g.strokeCircle(x, y, r);
      if (t >= 1) {
        handle.remove();
        g.destroy();
      }
    },
  });
}

